import React, { useState, useEffect } from 'react';
import { paymentService } from '../../services/paymentService';
import { billingService } from '../../services/billingService';
import { authService } from '../../authService';
import PaymentConfirmDialog from '../common/PaymentConfirmDialog';
import ConfirmationDialog from '../common/ConfirmationDialog';
import BillDetailsModal from '../billing/BillDetailsModal';
import BillModal from '../billing/BillModal';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  Divider,
  TableSortLabel,
  TablePagination,
  Collapse
} from '@mui/material';
import {
  Search as SearchIcon,
  Payment as PaymentIcon,
  Add as AddIcon,
  Receipt as ReceiptIcon,
  History as HistoryIcon,
  Print as PrintIcon,
  CheckCircle as CheckCircleIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  People as PeopleIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  Undo as UndoIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  UnfoldMore as UnfoldMoreIcon,
  UnfoldLess as UnfoldLessIcon
} from '@mui/icons-material';

const PaymentsPage = () => {
  const roundTo2 = (num) => Math.round(num * 100) / 100;
  
  const [activeTab, setActiveTab] = useState('search');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [customerTabs, setCustomerTabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showBillModal, setShowBillModal] = useState(false);
  const [billModalMode, setBillModalMode] = useState('add');
  const [selectedBillForModal, setSelectedBillForModal] = useState(null);
  const [preSelectedCustomerForModal, setPreSelectedCustomerForModal] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [billToDelete, setBillToDelete] = useState(null);
  
  // Filters
  const [filterBarangay, setFilterBarangay] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [barangays, setBarangays] = useState([]);
  const [customerTypes, setCustomerTypes] = useState([]);
  const [billCustomerTypes, setBillCustomerTypes] = useState([]);
  
  // Surcharge
  const [surchargeSettings, setSurchargeSettings] = useState(null);
  
  const [stats, setStats] = useState({
    todayCollection: 0,
    todayCount: 0,
    monthCollection: 0,
    monthCount: 0
  });
  
  // Recent Payments
  const [recentPayments, setRecentPayments] = useState([]);
  const [loadingRecentPayments, setLoadingRecentPayments] = useState(false);
  const [expandedPaymentRows, setExpandedPaymentRows] = useState({});
  const [allPaymentsExpanded, setAllPaymentsExpanded] = useState(false);

  const toggleExpandPaymentRow = (paymentId) => {
    setExpandedPaymentRows(prev => ({
      ...prev,
      [paymentId]: !prev[paymentId]
    }));
  };

  const handleToggleExpandAllPayments = (combinedPayments) => {
    if (allPaymentsExpanded) {
      setExpandedPaymentRows({});
      setAllPaymentsExpanded(false);
    } else {
      const newExpanded = {};
      combinedPayments.forEach(p => {
        newExpanded[p.id] = true;
      });
      setExpandedPaymentRows(newExpanded);
      setAllPaymentsExpanded(true);
    }
  };

  // Payment History Expansion State
  const [expandedHistoryRows, setExpandedHistoryRows] = useState({});
  const [allHistoryExpanded, setAllHistoryExpanded] = useState(false);

  const toggleExpandHistoryRow = (rowId) => {
    setExpandedHistoryRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  const handleToggleExpandAllHistory = (combinedHistory) => {
    if (allHistoryExpanded) {
      setExpandedHistoryRows({});
      setAllHistoryExpanded(false);
    } else {
      const newExpanded = {};
      combinedHistory.forEach(h => {
        newExpanded[h.id] = true;
      });
      setExpandedHistoryRows(newExpanded);
      setAllHistoryExpanded(true);
    }
  };
  
  // Payment Confirmation
  const [showPaymentConfirm, setShowPaymentConfirm] = useState(false);
  const [pendingPayment, setPendingPayment] = useState(null);
  
  // Mark as Unpaid
  const [showUnpaidConfirm, setShowUnpaidConfirm] = useState(false);
  const [billToMarkUnpaid, setBillToMarkUnpaid] = useState(null);

  // Bill Details Modal
  const [showBillDetails, setShowBillDetails] = useState(false);
  const [selectedBillDetails, setSelectedBillDetails] = useState(null);
  const [loadingBillDetails, setLoadingBillDetails] = useState(false);

  // Auto-Select By Amount
  const [showAutoSelectDialog, setShowAutoSelectDialog] = useState(false);
  const [autoSelectAmount, setAutoSelectAmount] = useState('');

  // Sorting for Payment History
  const [historySortConfig, setHistorySortConfig] = useState({ key: 'datepaid', direction: 'desc' });
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);

  const user = authService.getUserSession();

  useEffect(() => {
    const initializeData = async () => {
      loadStats();
      loadFilterOptions();
      const loadedSettings = await loadSurchargeSettings();
      await loadBillCustomerTypes();
      loadRecentPayments();
      
      const preSelectedCustomerData = sessionStorage.getItem('preSelectedCustomerForPayment');
      if (preSelectedCustomerData) {
        try {
          const customer = JSON.parse(preSelectedCustomerData);
          handleSelectCustomer(customer, loadedSettings);
          sessionStorage.removeItem('preSelectedCustomerForPayment');
        } catch (error) {
          console.error('Error parsing preselected customer:', error);
        }
      }
    };
    
    initializeData();
  }, []);
  
  useEffect(() => {
    const handleEditBillEvent = (event) => {
      const billToEdit = event.detail;
      handleEdit(billToEdit);
    };
    const handleDeleteBillEvent = (event) => {
      const billData = event.detail;
      const bill = typeof billData === 'object' ? billData : null;
      if (bill) {
        handleDeleteClick(bill);
      }
    };
    window.addEventListener('editBill', handleEditBillEvent);
    window.addEventListener('deleteBill', handleDeleteBillEvent);
    return () => {
      window.removeEventListener('editBill', handleEditBillEvent);
      window.removeEventListener('deleteBill', handleDeleteBillEvent);
    };
  }, [customerTabs]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(() => {
      handleSearch();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchTerm, filterBarangay, filterType, filterStatus]);

  const loadStats = async () => {
    const result = await paymentService.getPaymentStats();
    if (result.success) {
      setStats(result.data);
    }
  };

  const loadFilterOptions = async () => {
    try {
      const { data: customersData } = await paymentService.searchCustomers('');
      if (customersData) {
        const uniqueBarangays = [...new Set(customersData.map(c => c.barangay).filter(Boolean))];
        setBarangays(uniqueBarangays.map(b => ({ barangay: b })));
        
        const uniqueTypes = [...new Set(customersData.map(c => c.type).filter(Boolean))];
        setCustomerTypes(uniqueTypes.map(t => ({ type: t })));
      }
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const loadSurchargeSettings = async () => {
    const result = await paymentService.getSurchargeSettings();
    if (result.success) {
      setSurchargeSettings(result.data);
      return result.data;
    }
    return null;
  };
  
  const loadBillCustomerTypes = async () => {
    const result = await billingService.getCustomerTypes();
    if (result.success) {
      setBillCustomerTypes(result.data);
    }
  };

  const loadRecentPayments = async () => {
    setLoadingRecentPayments(true);
    const result = await paymentService.getRecentPayments(30);
    if (result.success) {
      setRecentPayments(result.data);
    }
    setLoadingRecentPayments(false);
  };

  const handleProcessPayment = async (amountReceived, creditToApply, newCredit, waiveSurcharge = false) => {
    if (!pendingPayment) return;
    
    setLoading(true);

    const involvedCustomers = pendingPayment.customers && pendingPayment.customers.length > 0
      ? pendingPayment.customers
      : (pendingPayment.customer ? [pendingPayment.customer] : []);

    const selectedBillsData = (pendingPayment.selectedBillsData && pendingPayment.selectedBillsData.length > 0)
      ? pendingPayment.selectedBillsData
      : (getActiveCustomerTab()?.billsWithSurcharge || []).filter(bill => pendingPayment.bills?.includes(bill.billid));

    // Map bills to payment format
    const billsData = selectedBillsData.map(bill => {
      const surchargeAmount = roundTo2(waiveSurcharge ? 0 : (bill.surchargeInfo?.surchargeAmount || bill.surchargeamount || 0));
      const basicAmount = roundTo2(parseFloat(bill.basicamount || 0));
      const discountAmount = roundTo2(bill.calculatedDiscountAmount || bill.discountamount || 0);
      const basicAfterDiscount = roundTo2(Math.max(0, basicAmount - discountAmount));
      const totalAmount = roundTo2(basicAfterDiscount + surchargeAmount);
      const creditForThisBill = roundTo2(parseFloat(bill.creditApplied || 0));

      return {
        billid: bill.billid,
        customerid: bill.customerid || bill.customer?.customerid,
        basicamount: basicAmount,
        surchargeamount: surchargeAmount,
        discountamount: discountAmount,
        totalbillamount: totalAmount,
        advancepaymentamount: creditForThisBill
      };
    });

    // Build credit adjustments map per customer
    const creditAdjustmentsMap = {};
    involvedCustomers.forEach(cust => {
      const custBills = selectedBillsData.filter(b => (b.customerid === cust.customerid || b.customer?.customerid === cust.customerid));
      const custCreditApplied = roundTo2(custBills.reduce((sum, b) => sum + parseFloat(b.creditApplied || 0), 0));
      
      const breakdown = (pendingPayment.customerBreakdowns || []).find(cb => cb.customerid === cust.customerid);
      const custNewCredit = breakdown ? (breakdown.excessPayment || 0) : ((!pendingPayment.isMultiCustomer) ? newCredit : 0);
      const custCreditAdj = roundTo2(custNewCredit - custCreditApplied);
      creditAdjustmentsMap[cust.customerid] = custCreditAdj;
    });

    const result = await paymentService.processPayment(
      billsData,
      `${user.firstname} ${user.lastname}`,
      amountReceived,
      creditAdjustmentsMap
    );

    if (result.success) {
      let message = `Successfully processed payment for ${result.data.paidCount} bill(s)`;
      if (involvedCustomers.length > 1) {
        message += ` across ${involvedCustomers.length} customer accounts!`;
      } else {
        message += '!';
      }
      if (creditToApply > 0) {
        message += ` Credit of ₱${creditToApply.toFixed(2)} was applied.`;
      }
      if (newCredit > 0) {
        message += ` New credit of ₱${newCredit.toFixed(2)} added to account.`;
      }
      setSuccess(message);

      // Clear selections on all tabs
      setCustomerTabs(prevTabs =>
        prevTabs.map(tab => ({ ...tab, selectedBills: [], autoSelectedAmount: null }))
      );

      // Reload data for all involved customers
      for (const cust of involvedCustomers) {
        await reloadCustomerData(cust.customerid);
      }
      loadStats();
      loadRecentPayments();
      setTimeout(() => setSuccess(''), 5000);
    } else {
      setError(result.error);
    }
    setLoading(false);
    setShowPaymentConfirm(false);
    setPendingPayment(null);
  };

  const handleMarkAsUnpaid = async () => {
    if (!billToMarkUnpaid) return;
    
    setLoading(true);
    const result = await paymentService.markBillAsUnpaid(billToMarkUnpaid.billid);
    
    if (result.success) {
      setSuccess(`Bill #${billToMarkUnpaid.billid} marked as unpaid successfully!`);
      await reloadCustomerData(billToMarkUnpaid.customerid);
      loadStats();
      loadRecentPayments();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error);
    }
    setLoading(false);
    setShowUnpaidConfirm(false);
    setBillToMarkUnpaid(null);
  };

  const getActiveCustomerTab = () => {
    return customerTabs.find(tab => tab.customer.customerid === activeTab);
  };

  const calculateBillsWithSurcharge = (bills, customer, settings = null) => {
    const settingsToUse = settings || surchargeSettings;
    if (!settingsToUse) return bills;
    
    // Sort bills by date (oldest first) to ensure credit applies correctly
    const sortedBills = [...bills].sort((a, b) => new Date(a.billedmonth) - new Date(b.billedmonth));
    
    let remainingCredit = customer ? parseFloat(customer.credit_balance || 0) : 0;
    
    return sortedBills.map(bill => {
      const surchargeInfo = typeof paymentService.calculateSurcharge === 'function'
        ? paymentService.calculateSurcharge(bill, settingsToUse)
        : { surchargeAmount: parseFloat(bill.surchargeamount || 0), daysOverdue: 0 };
      
      const basicAmount = roundTo2(parseFloat(bill.basicamount || 0));
      const surchargeAmount = roundTo2(surchargeInfo ? surchargeInfo.surchargeAmount : parseFloat(bill.surchargeamount || 0));
      
      let calculatedDiscountAmount = 0;
      const consumption = parseFloat(bill.consumption || 0);

      // Discount logic: Deducted from basic amount if consumption > 1
      if (consumption > 1) {
        if (customer && customer.discount) {
          const customerDiscountPercent = parseFloat(customer.discount);
          calculatedDiscountAmount = roundTo2((basicAmount * customerDiscountPercent) / 100);
        } else if (bill.discountamount && parseFloat(bill.discountamount) > 0) {
          calculatedDiscountAmount = roundTo2(parseFloat(bill.discountamount));
        }
      }
      
      const basicAfterDiscount = roundTo2(Math.max(0, basicAmount - calculatedDiscountAmount));
      
      // Apply credit strictly to basic amount; if credit > basic, excess rolls over to next bill
      let creditApplied = 0;
      if (remainingCredit > 0) {
        creditApplied = roundTo2(Math.min(remainingCredit, basicAfterDiscount));
        remainingCredit = roundTo2(remainingCredit - creditApplied);
      }
      
      const netBasic = roundTo2(basicAfterDiscount - creditApplied);
      const totalBeforeCredit = roundTo2(basicAfterDiscount + surchargeAmount);
      const totalAfterCredit = roundTo2(netBasic + surchargeAmount);
      
      return {
        ...bill,
        surchargeInfo,
        calculatedDiscountAmount,
        calculatedTotal: totalBeforeCredit,
        netTotal: totalAfterCredit,
        creditApplied: creditApplied
      };
    });
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setError('Please enter a customer name or ID');
      return;
    }

    setLoading(true);
    setError('');
    const result = await paymentService.searchCustomers(searchTerm);
    
    if (result.success) {
      let filteredData = result.data;
      
      if (filterBarangay !== 'all') {
        filteredData = filteredData.filter(c => c.barangay?.toLowerCase() === filterBarangay.toLowerCase());
      }
      if (filterType !== 'all') {
        filteredData = filteredData.filter(c => c.type?.toLowerCase() === filterType.toLowerCase());
      }
      if (filterStatus !== 'all') {
        filteredData = filteredData.filter(c => c.status?.toLowerCase() === filterStatus.toLowerCase());
      }
      
      setSearchResults(filteredData);
      if (filteredData.length === 0) {
        setError(`No customers found matching "${searchTerm}" with current filters.`);
      }
    } else {
      setError(`Search error: ${result.error}`);
    }
    setLoading(false);
  };

  const handleBrowseAll = async () => {
    setLoading(true);
    setError('');
    setSearchTerm('');
    const result = await paymentService.searchCustomers('');
    
    if (result.success) {
      let filteredData = result.data;
      
      if (filterBarangay !== 'all') {
        filteredData = filteredData.filter(c => c.barangay?.toLowerCase() === filterBarangay.toLowerCase());
      }
      if (filterType !== 'all') {
        filteredData = filteredData.filter(c => c.type?.toLowerCase() === filterType.toLowerCase());
      }
      if (filterStatus !== 'all') {
        filteredData = filteredData.filter(c => c.status?.toLowerCase() === filterStatus.toLowerCase());
      }
      
      setSearchResults(filteredData);
      if (filteredData.length === 0) {
        setError('No customers found with current filters.');
      }
    } else {
      setError(`Error loading customers: ${result.error}`);
    }
    setLoading(false);
  };

  const handleSelectCustomer = async (customer, providedSettings = null) => {
    const existingTab = customerTabs.find(tab => tab.customer.customerid === customer.customerid);
    
    if (existingTab) {
      console.error('DEBUG: Tab exists, switching to:', customer.customerid);
      setActiveTab(customer.customerid);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const customerDetailsResult = await paymentService.getCustomerDetails(customer.customerid);
      const customerWithCredit = customerDetailsResult.success ? customerDetailsResult.data : customer;

      const unpaidResult = await paymentService.getUnpaidBills(customer.customerid);
      const unpaidBills = unpaidResult.success ? unpaidResult.data : [];

      const historyResult = await paymentService.getPaymentHistory(customer.customerid);
      const paymentHistory = historyResult.success ? historyResult.data : [];

      const billsWithSurcharge = calculateBillsWithSurcharge(unpaidBills, customerWithCredit, providedSettings);
      const newTab = {
        customer: customerWithCredit,
        unpaidBills,
        paymentHistory,
        billsWithSurcharge,
        selectedBills: [],
        creditBalance: parseFloat(customerWithCredit.credit_balance || 0)
      };

      setCustomerTabs(prev => {
        return [...prev, newTab];
      });
      setActiveTab(customer.customerid);
    } catch (err) {
      console.error('Error in handleSelectCustomer:', err);
      setError('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  };
  
  const openAddModal = (customer = null) => {
    setBillModalMode('add');
    setSelectedBillForModal(null);
    setPreSelectedCustomerForModal(customer);
    setShowBillModal(true);
  };
  
  const handleEdit = (bill) => {
    setBillModalMode('edit');
    setSelectedBillForModal(bill);
    setShowBillModal(true);
  };
  
  const handleDeleteClick = (bill) => {
    setBillToDelete(bill);
    setShowDeleteConfirm(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!billToDelete) return;
    setLoading(true);
    const result = await billingService.deleteBill(billToDelete.billid);
    if (result.success) {
      setSuccess('Bill deleted successfully');
      await reloadCustomerData(billToDelete.customerid);
      loadStats();
      loadRecentPayments();
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error);
    }
    setLoading(false);
    setShowDeleteConfirm(false);
    setBillToDelete(null);
  };
  
  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setBillToDelete(null);
  };

  const handleCloseTab = (customerid) => {
    const updatedTabs = customerTabs.filter(tab => tab.customer.customerid !== customerid);
    setCustomerTabs(updatedTabs);
    
    if (activeTab === customerid) {
      if (updatedTabs.length > 0) {
        setActiveTab(updatedTabs[updatedTabs.length - 1].customer.customerid);
      } else {
        setActiveTab('search');
      }
    }
  };

  const updateSelectedBills = (bills, autoAmount = null) => {
    setCustomerTabs(prevTabs =>
      prevTabs.map(tab =>
        tab.customer.customerid === activeTab
          ? { ...tab, selectedBills: bills, autoSelectedAmount: autoAmount }
          : tab
      )
    );
  };

  const getGlobalSelectedSummary = () => {
    const tabsWithSelection = customerTabs.filter(tab => tab.selectedBills && tab.selectedBills.length > 0);
    const totalBillsCount = tabsWithSelection.reduce((sum, tab) => sum + tab.selectedBills.length, 0);

    const allSelectedBills = [];
    tabsWithSelection.forEach(tab => {
      const selected = (tab.billsWithSurcharge || [])
        .filter(b => tab.selectedBills.includes(b.billid))
        .map(b => ({
          ...b,
          customer: tab.customer,
          customerid: tab.customer.customerid
        }));
      allSelectedBills.push(...selected);
    });

    const customerBreakdowns = tabsWithSelection.map(tab => {
      const custBills = (tab.billsWithSurcharge || [])
        .filter(b => tab.selectedBills.includes(b.billid))
        .map(b => ({
          ...b,
          customer: tab.customer,
          customerid: tab.customer.customerid
        }));

      const custBasic = roundTo2(custBills.reduce((sum, b) => sum + parseFloat(b.basicamount || 0), 0));
      const custSurcharge = roundTo2(custBills.reduce((sum, b) => sum + parseFloat(b.surchargeInfo?.surchargeAmount || b.surchargeamount || 0), 0));
      const custDiscount = roundTo2(custBills.reduce((sum, b) => sum + parseFloat(b.calculatedDiscountAmount || b.discountamount || 0), 0));
      const custCreditApplied = roundTo2(custBills.reduce((sum, b) => sum + parseFloat(b.creditApplied || 0), 0));
      const custNetPayable = roundTo2(custBills.reduce((sum, b) => sum + parseFloat(b.netTotal !== undefined ? b.netTotal : (b.calculatedTotal || b.totalbillamount || 0)), 0));
      
      const parsedAuto = (tab.autoSelectedAmount !== null && tab.autoSelectedAmount !== undefined && tab.autoSelectedAmount !== '')
        ? roundTo2(parseFloat(tab.autoSelectedAmount))
        : null;

      const custExcess = (parsedAuto !== null && parsedAuto > custNetPayable) ? roundTo2(parsedAuto - custNetPayable) : 0;
      const custEffectivePayment = (parsedAuto !== null && parsedAuto > custNetPayable) ? parsedAuto : custNetPayable;

      return {
        customer: tab.customer,
        customerid: tab.customer.customerid,
        name: tab.customer.name,
        accountno: tab.customer.accountnumber || tab.customer.accountno || tab.customer.oldaccountno || tab.customer.customerid,
        barangay: tab.customer.barangay,
        selectedBills: custBills,
        selectedBillsCount: custBills.length,
        basicAmount: custBasic,
        surchargeAmount: custSurcharge,
        discountAmount: custDiscount,
        creditApplied: custCreditApplied,
        creditBalance: parseFloat(tab.customer?.credit_balance || 0),
        netAmountPayable: custNetPayable,
        autoSelectedAmount: tab.autoSelectedAmount,
        excessPayment: custExcess,
        effectivePayment: custEffectivePayment
      };
    });

    const totalBasic = roundTo2(allSelectedBills.reduce((sum, b) => sum + parseFloat(b.basicamount || 0), 0));
    const totalSurcharge = roundTo2(allSelectedBills.reduce((sum, b) => sum + parseFloat(b.surchargeInfo?.surchargeAmount || b.surchargeamount || 0), 0));
    const totalDiscount = roundTo2(allSelectedBills.reduce((sum, b) => sum + parseFloat(b.calculatedDiscountAmount || b.discountamount || 0), 0));
    const totalCreditApplied = roundTo2(allSelectedBills.reduce((sum, b) => sum + parseFloat(b.creditApplied || 0), 0));
    const totalCreditBalance = roundTo2(tabsWithSelection.reduce((sum, tab) => sum + parseFloat(tab.customer?.credit_balance || 0), 0));
    const totalAmount = roundTo2(allSelectedBills.reduce((sum, b) => sum + parseFloat(b.calculatedTotal || b.totalbillamount || 0), 0));
    const netAmountPayable = roundTo2(allSelectedBills.reduce((sum, b) => sum + parseFloat(b.netTotal !== undefined ? b.netTotal : (b.calculatedTotal || b.totalbillamount || 0)), 0));
    const totalExcessPayment = roundTo2(customerBreakdowns.reduce((sum, c) => sum + c.excessPayment, 0));
    const totalEffectivePayment = roundTo2(customerBreakdowns.reduce((sum, c) => sum + c.effectivePayment, 0));

    return {
      tabsWithSelection,
      customersCount: tabsWithSelection.length,
      customers: tabsWithSelection.map(t => t.customer),
      customerBreakdowns,
      totalBillsCount,
      allSelectedBills,
      totalBasic,
      totalSurcharge,
      totalDiscount,
      totalCreditApplied,
      totalCreditBalance,
      totalAmount,
      netAmountPayable,
      totalExcessPayment,
      totalEffectivePayment
    };
  };

  const handleClearAllSelections = () => {
    setCustomerTabs(prevTabs =>
      prevTabs.map(tab => ({ ...tab, selectedBills: [], autoSelectedAmount: null }))
    );
  };

  const reloadCustomerData = async (customerid) => {
    const customerDetailsResult = await paymentService.getCustomerDetails(customerid);
    const unpaidResult = await paymentService.getUnpaidBills(customerid);
    const historyResult = await paymentService.getPaymentHistory(customerid);

    const newUnpaidBills = unpaidResult.success ? unpaidResult.data : [];
    const customerWithCredit = customerDetailsResult.success ? customerDetailsResult.data : null;
    const billsWithSurcharge = calculateBillsWithSurcharge(newUnpaidBills, customerWithCredit);

    setCustomerTabs(prevTabs =>
      prevTabs.map(tab =>
        tab.customer.customerid === customerid
          ? {
              ...tab,
              customer: customerWithCredit || tab.customer,
              unpaidBills: newUnpaidBills,
              paymentHistory: historyResult.success ? historyResult.data : [],
              billsWithSurcharge,
              creditBalance: customerWithCredit ? parseFloat(customerWithCredit.credit_balance || 0) : 0,
              selectedBills: []
            }
          : tab
      )
    );
  };
  
  const handleModalSuccess = async (message) => {
    setSuccess(message);
    const customerId =
      preSelectedCustomerForModal?.customerid ||
      selectedBillForModal?.customerid ||
      getActiveCustomerTab()?.customer?.customerid ||
      null;
    if (customerId) {
      await reloadCustomerData(customerId);
    }
    setPreSelectedCustomerForModal(null);
    loadStats();
    loadRecentPayments();
    setTimeout(() => setSuccess(''), 4000);
  };

  const formatCurrency = (amount) => {
    return `₱${parseFloat(amount || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatMonth = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'long'
    });
  };

  const getCombinedRecentPayments = (payments) => {
    if (!payments || payments.length === 0) return [];

    const groupedMap = new Map();

    payments.forEach(payment => {
      const dateKey = payment.datepaid 
        ? new Date(payment.datepaid).toLocaleDateString('en-PH')
        : 'no-date';
      const key = `${dateKey}_${payment.customerid}`;

      if (!groupedMap.has(key)) {
        groupedMap.set(key, {
          id: key,
          customerid: payment.customerid,
          customer: payment.customer,
          datepaid: payment.datepaid,
          billid: payment.billid,
          bills: [],
          totalbillamount: 0,
          billedMonthsList: []
        });
      }

      const group = groupedMap.get(key);
      group.bills.push(payment);
      group.totalbillamount += parseFloat(payment.totalbillamount || 0);

      const formattedMonth = formatMonth(payment.billedmonth);
      if (formattedMonth && formattedMonth !== 'N/A' && !group.billedMonthsList.includes(formattedMonth)) {
        group.billedMonthsList.push(formattedMonth);
      }
    });

    return Array.from(groupedMap.values()).map(group => ({
      ...group,
      billedMonthsFormatted: group.billedMonthsList.join(', ') || 'N/A'
    }));
  };

  const getCombinedPaymentHistory = (history) => {
    if (!history || history.length === 0) return [];

    const groupedMap = new Map();

    history.forEach(bill => {
      const dateKey = bill.datepaid 
        ? new Date(bill.datepaid).toLocaleDateString('en-PH')
        : (bill.billid ? `bill_${bill.billid}` : 'no-date');

      if (!groupedMap.has(dateKey)) {
        groupedMap.set(dateKey, {
          id: dateKey,
          datepaid: bill.datepaid,
          billid: bill.billid,
          customerid: bill.customerid,
          bills: [],
          totalbillamount: 0,
          consumption: 0,
          billedMonthsList: [],
          paymentstatus: bill.paymentstatus || 'Paid',
          previousreading: bill.previousreading,
          currentreading: bill.currentreading
        });
      }

      const group = groupedMap.get(dateKey);
      group.bills.push(bill);
      group.totalbillamount += parseFloat(bill.totalbillamount || 0);
      group.consumption += parseFloat(bill.consumption || 0);

      const formattedMonth = formatMonth(bill.billedmonth);
      if (formattedMonth && formattedMonth !== 'N/A' && !group.billedMonthsList.includes(formattedMonth)) {
        group.billedMonthsList.push(formattedMonth);
      }
    });

    return Array.from(groupedMap.values()).map(group => ({
      ...group,
      billedmonth: group.billedMonthsList.join(', ') || 'N/A'
    }));
  };

  const handleViewBillDetails = async (billid, customerid) => {
    setLoadingBillDetails(true);
    setShowBillDetails(true);
    
    try {
      const customerResult = await paymentService.getCustomerDetails(customerid);
      const customerData = customerResult.success ? customerResult.data : null;

      const billResult = await paymentService.getUnpaidBills(customerid);
      const historyResult = await paymentService.getPaymentHistory(customerid);
      
      let billData = null;
      let isUnpaid = false;
      if (billResult.success) {
        billData = billResult.data.find(b => b.billid === billid);
        if (billData) isUnpaid = true;
      }
      if (!billData && historyResult.success) {
        billData = historyResult.data.find(b => b.billid === billid);
      }

      if (billData && customerData) {
        // We also want to pass the full history to the modal for the graph
        const fullHistory = historyResult.success ? historyResult.data : [];
        
        let enrichedBill = billData;
        
        if (isUnpaid) {
          // For unpaid bills, calculate surcharge and credit allocation
          // Must process ALL unpaid bills to ensure correct "Oldest First" credit application
          const billsWithSurcharge = calculateBillsWithSurcharge(billResult.data, customerData);
          enrichedBill = billsWithSurcharge.find(b => b.billid === billid) || billData;
        } else {
          // For paid bills, use existing data (maybe map fields if necessary for consistency)
          // Ensure creditApplied is present (from advancepaymentamount usually)
          enrichedBill = {
            ...billData,
            creditApplied: billData.advancepaymentamount || 0,
            calculatedTotal: billData.totalbillamount
          };
        }
        
        setSelectedBillDetails({
          bill: enrichedBill,
          customer: customerData,
          history: fullHistory
        });
      } else {
        setError('Unable to load bill details');
        setShowBillDetails(false);
      }
    } catch (error) {
      console.error('Error loading bill details:', error);
      setError('Error loading bill details');
      setShowBillDetails(false);
    }
    
    setLoadingBillDetails(false);
  };

  const handleAutoSelectByAmount = () => {
    const amount = parseFloat(autoSelectAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    const currentTab = getActiveCustomerTab();
    if (!currentTab) return;

    const { billsWithSurcharge } = currentTab;
    
    // Sort bills by date (oldest first)
    const sortedBills = [...billsWithSurcharge].sort((a, b) => {
      const dateA = new Date(a.billedmonth);
      const dateB = new Date(b.billedmonth);
      return dateA - dateB;
    });

    let remainingAmount = amount;
    const billsToSelect = [];
    const selectedMonths = [];
    let nextBillMonth = null;

    for (const bill of sortedBills) {
      // Calculate total amount for this bill including surcharge
      // Logic mirrors the calculation in the table
      const surcharge = bill.surchargeInfo?.surchargeAmount || 0;
      const basic = parseFloat(bill.basicamount || 0);
      const discount = parseFloat(bill.calculatedDiscountAmount || 0); // Assuming discount logic exists or included in netTotal
      
      // Use netTotal if available, otherwise calculatedTotal, otherwise basic+surcharge
      // Note: In the table, we use bill.netTotal !== undefined ? bill.netTotal : (bill.calculatedTotal || bill.totalbillamount)
      // However, netTotal might already include credit deduction if we implemented it in state.
      // But for selection purposes, we care about the amount *to be paid*.
      // If credit is already applied, netTotal is the remaining to pay.
      // Let's rely on netTotal which should be the source of truth for "Amount Due".
      
      const billTotal = parseFloat(bill.netTotal !== undefined ? bill.netTotal : (bill.calculatedTotal || bill.totalbillamount));
      
      if (remainingAmount >= billTotal) {
        billsToSelect.push(bill.billid);
        selectedMonths.push(formatMonth(bill.billedmonth));
        remainingAmount -= billTotal;
      } else {
        // Amount insufficient to cover this bill fully
        // Stop selection as per "oldest first" rule
        nextBillMonth = formatMonth(bill.billedmonth);
        break;
      }
    }

    if (billsToSelect.length > 0) {
      updateSelectedBills(billsToSelect, amount);
      
      let message = `Automatically selected ${billsToSelect.length} bill(s)`;
      if (selectedMonths.length > 0) {
        message += ` covering ${selectedMonths.join(', ')}`;
      }
      message += `.`;
      
      if (remainingAmount > 0) {
        if (nextBillMonth) {
           message += ` Remaining amount of ${formatCurrency(remainingAmount)} will be applied on ${nextBillMonth}.`;
        } else {
           message += ` Remaining amount: ${formatCurrency(remainingAmount)}.`;
        }
      }

      setSuccess(message);
      setShowAutoSelectDialog(false);
      setAutoSelectAmount('');
    } else {
      setError('Amount is insufficient to cover the oldest unpaid bill.');
    }
  };

  const handleHistorySort = (key) => {
    let direction = 'asc';
    if (historySortConfig.key === key && historySortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setHistorySortConfig({ key, direction });
  };

  const getSortedHistory = (history) => {
    if (!history) return [];
    
    const sorted = [...history].sort((a, b) => {
      let aValue = a[historySortConfig.key];
      let bValue = b[historySortConfig.key];
      
      // Handle special cases
      if (historySortConfig.key === 'amount') {
        aValue = parseFloat(a.totalbillamount || 0);
        bValue = parseFloat(b.totalbillamount || 0);
      } else if (historySortConfig.key === 'consumption') {
        aValue = parseFloat(a.consumption || 0);
        bValue = parseFloat(b.consumption || 0);
      }
      
      if (aValue < bValue) return historySortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return historySortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  };

  const calculateReceiptDetails = (allBillsWithSurcharge, selectedBillIds, customer, customPaymentAmount = null) => {
    const selectedBillsData = (allBillsWithSurcharge || [])
      .filter(bill => selectedBillIds.includes(bill.billid))
      .sort((a, b) => new Date(a.billedmonth) - new Date(b.billedmonth));

    const totalBasicSelected = roundTo2(selectedBillsData.reduce((sum, b) => sum + parseFloat(b.basicamount || 0), 0));
    const totalSurcharge = roundTo2(selectedBillsData.reduce((sum, b) => sum + parseFloat(b.surchargeInfo?.surchargeAmount || b.surchargeamount || 0), 0));
    const totalDiscountSelected = roundTo2(selectedBillsData.reduce((sum, b) => sum + parseFloat(b.calculatedDiscountAmount || b.discountamount || 0), 0));
    const totalCreditAppliedSelected = roundTo2(selectedBillsData.reduce((sum, b) => sum + parseFloat(b.creditApplied || 0), 0));
    const defaultTotalPayable = roundTo2(selectedBillsData.reduce((sum, b) => sum + parseFloat(b.netTotal !== undefined ? b.netTotal : (b.calculatedTotal || b.totalbillamount || 0)), 0));

    const parsedPayment = parseFloat(customPaymentAmount);
    const targetPayment = (customPaymentAmount !== null && customPaymentAmount !== undefined && customPaymentAmount !== '' && !isNaN(parsedPayment) && parsedPayment > 0)
      ? roundTo2(parsedPayment)
      : defaultTotalPayable;

    // Surcharges from the selected/overdue bills are accounted for in the target payment
    const netCashForBasic = roundTo2(Math.max(0, targetPayment - totalSurcharge));
    let remainingCash = netCashForBasic;

    const balanceItems = [];
    const fullMonthBills = [];
    const partialItems = [];
    let unbilledAdvanceCash = 0;

    // Sort all customer unpaid bills chronologically
    const allSortedBills = [...(allBillsWithSurcharge || [])].sort((a, b) => new Date(a.billedmonth) - new Date(b.billedmonth));

    for (const bill of allSortedBills) {
      const isSelected = selectedBillIds.includes(bill.billid);
      const bBasic = roundTo2(parseFloat(bill.basicamount || 0));
      const bDiscount = roundTo2(parseFloat(bill.calculatedDiscountAmount || 0));
      const bCredit = roundTo2(parseFloat(bill.creditApplied || 0));
      const netBasicDue = roundTo2(Math.max(0, bBasic - bDiscount - bCredit));

      if (isSelected) {
        if (remainingCash >= netBasicDue && netBasicDue > 0) {
          if (bCredit > 0) {
            const deductionParts = [`₱${bCredit.toFixed(2)} credit`];
            if (bDiscount > 0) deductionParts.push(`₱${bDiscount.toFixed(2)} discount`);
            const deductionLabel = deductionParts.join(' & ');
            balanceItems.push({
              label: `Balance of ${formatMonth(bill.billedmonth)}`,
              amount: netBasicDue,
              type: 'balance_basic',
              month: formatMonth(bill.billedmonth),
              deductionNote: `₱${bBasic.toFixed(2)} basic less ${deductionLabel}`,
              deductionApplied: roundTo2(bCredit + bDiscount)
            });
          } else {
            fullMonthBills.push(bill);
          }
          remainingCash = roundTo2(remainingCash - netBasicDue);
        } else if (netBasicDue <= 0 && bCredit > 0) {
          balanceItems.push({
            label: `Balance of ${formatMonth(bill.billedmonth)}`,
            amount: 0,
            type: 'credit_covered',
            month: formatMonth(bill.billedmonth),
            deductionNote: `₱${bBasic.toFixed(2)} basic fully covered by active credit`,
            deductionApplied: bCredit
          });
        }
      } else {
        // Upcoming unselected bill
        if (remainingCash >= netBasicDue && netBasicDue > 0) {
          fullMonthBills.push(bill);
          remainingCash = roundTo2(remainingCash - netBasicDue);
        } else if (remainingCash > 0) {
          partialItems.push({
            label: `Partial Payment of ${formatMonth(bill.billedmonth)}`,
            amount: remainingCash,
            type: 'partial_basic',
            month: formatMonth(bill.billedmonth),
            note: `₱${remainingCash.toFixed(2)} advance payment applied to upcoming bill`
          });
          remainingCash = 0;
          break;
        } else if (bCredit > 0) {
          partialItems.push({
            label: `Partial Credit applied to ${formatMonth(bill.billedmonth)}`,
            amount: bCredit,
            type: 'credit_applied_upcoming',
            month: formatMonth(bill.billedmonth),
            note: `₱${bCredit.toFixed(2)} active credit applied to upcoming bill`
          });
        }
      }
    }

    if (remainingCash > 0) {
      unbilledAdvanceCash = remainingCash;
    }

    const lineItems = [];

    // 1. Balance of specific billed months
    balanceItems.forEach(item => {
      lineItems.push({
        lineNo: lineItems.length + 1,
        ...item
      });
    });

    // 2. Months covered in full (COMBINED into single line)
    if (fullMonthBills.length > 0) {
      const totalFullAmount = roundTo2(fullMonthBills.reduce((sum, b) => {
        const basic = parseFloat(b.basicamount || 0);
        const discount = parseFloat(b.calculatedDiscountAmount || b.discountamount || 0);
        return sum + Math.max(0, basic - discount);
      }, 0));
      let combinedLabel = '';
      if (fullMonthBills.length === 1) {
        combinedLabel = `Water Bill (${formatMonth(fullMonthBills[0].billedmonth)})`;
      } else {
        const firstM = formatMonth(fullMonthBills[0].billedmonth);
        const lastM = formatMonth(fullMonthBills[fullMonthBills.length - 1].billedmonth);
        combinedLabel = `Water Bill (${firstM} - ${lastM})`;
      }

      lineItems.push({
        lineNo: lineItems.length + 1,
        label: combinedLabel,
        amount: totalFullAmount,
        type: 'combined_full_basic',
        monthsCount: fullMonthBills.length,
        monthsList: fullMonthBills.map(b => formatMonth(b.billedmonth)).join(', ')
      });
    }

    // 3. Partial payment for specific billed month
    partialItems.forEach(item => {
      lineItems.push({
        lineNo: lineItems.length + 1,
        ...item
      });
    });

    // 4. Advance unbilled cash
    if (unbilledAdvanceCash > 0) {
      const latestBill = allSortedBills[allSortedBills.length - 1];
      let nextMonthLabel = '';
      if (latestBill && latestBill.billedmonth) {
        const nextDate = new Date(latestBill.billedmonth);
        nextDate.setMonth(nextDate.getMonth() + 1);
        nextMonthLabel = formatMonth(nextDate);
      } else {
        const nextDate = new Date();
        nextDate.setMonth(nextDate.getMonth() + 1);
        nextMonthLabel = formatMonth(nextDate);
      }

      lineItems.push({
        lineNo: lineItems.length + 1,
        label: `Partial Payment of ${nextMonthLabel}`,
        amount: unbilledAdvanceCash,
        type: 'partial_basic',
        month: nextMonthLabel,
        note: `₱${unbilledAdvanceCash.toFixed(2)} advance payment applied to upcoming bill`
      });
    }

    // 5. Add Surcharge line
    if (totalSurcharge > 0) {
      lineItems.push({
        lineNo: lineItems.length + 1,
        label: 'Surcharge',
        amount: totalSurcharge,
        type: 'surcharge'
      });
    }

    const totalReceiptAmount = roundTo2(lineItems.reduce((sum, item) => sum + item.amount, 0));
    const currentCustomerCredit = parseFloat(customer?.credit_balance || 0);
    const remainingCreditBalance = roundTo2(Math.max(0, currentCustomerCredit - totalCreditAppliedSelected));

    return {
      selectedBillsCount: selectedBillsData.length,
      defaultTotalPayable,
      targetPayment,
      totalBasic: totalBasicSelected,
      totalSurcharge,
      totalDiscount: totalDiscountSelected,
        totalCreditApplied: totalCreditAppliedSelected,
        netCashForBasic,
        totalReceiptAmount,
        lineItems,
        remainingCreditBalance,
        customerDiscountPercent: customer?.discount ? parseFloat(customer.discount) : 0
    };
  };

  const handlePrintReceipt = (breakdownData = null, customerData = null, customerBreakdownsData = null) => {
    const currentTab = getActiveCustomerTab();
    const customer = customerData || currentTab?.customer;
    const isMulti = Array.isArray(customer) ? customer.length > 1 : false;
    const primaryCustomer = Array.isArray(customer) ? customer[0] : customer;

    const breakdown = breakdownData || (currentTab ? calculateReceiptDetails(
      currentTab.billsWithSurcharge,
      currentTab.selectedBills,
      primaryCustomer
    ) : null);

    if (!primaryCustomer || !breakdown) {
      setError('Unable to prepare receipt print');
      return;
    }

    const custBreakdowns = customerBreakdownsData 
      || breakdown?.customerBreakdowns 
      || (isMulti ? (getGlobalSelectedSummary()?.customerBreakdowns || []) : []);

    const currentDate = new Date().toLocaleDateString('en-PH', { 
      year: 'numeric', month: 'short', day: 'numeric', 
      hour: '2-digit', minute: '2-digit' 
    });

    const cashierName = user ? `${user.firstname || ''} ${user.lastname || ''}`.trim() : 'Authorized Collecting Officer';

    const openFn = typeof globalThis.open === 'function' ? globalThis.open : window.open;
    let printWindow = null;
    try {
      printWindow = openFn('', '', 'width=780,height=750');
    } catch (e) {
      console.error('Error opening print window:', e);
      printWindow = null;
    }

    if (!printWindow || !printWindow.document) {
      setError('Unable to open print window. Please allow pop-ups and try again.');
      return;
    }

    const title = isMulti 
      ? `Receipt Details Slip (Combined Accounts)` 
      : `Receipt Details Slip - ${primaryCustomer.name}`;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title}</title>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500;700&display=swap" rel="stylesheet">
        <style>
          * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
          }
          body {
            font-family: 'Inter', -apple-system, sans-serif;
            color: #0f172a;
            background: #ffffff;
            padding: 24px;
            font-size: 12px;
            line-height: 1.45;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .slip-container {
            max-width: 660px;
            margin: 0 auto;
            border: 1px solid #cbd5e1;
            border-radius: 8px;
            padding: 20px 24px;
            background: #ffffff;
          }
          .header {
            text-align: center;
            border-bottom: 1.5px solid #0f172a;
            padding-bottom: 10px;
            margin-bottom: 14px;
          }
          .header .lgu-title {
            font-size: 10px;
            font-weight: 700;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 0.8px;
          }
          .header .lgu-name {
            font-size: 15px;
            font-weight: 800;
            color: #0f172a;
            letter-spacing: -0.2px;
            margin: 1px 0;
          }
          .header .office-name {
            font-size: 11px;
            font-weight: 600;
            color: #334155;
          }
          .header .badge {
            display: inline-block;
            background: #0f172a;
            color: #ffffff;
            font-size: 9.5px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            padding: 2px 8px;
            border-radius: 3px;
            margin-top: 6px;
          }
          .meta-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px 16px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 6px;
            padding: 10px 12px;
            margin-bottom: 14px;
          }
          .meta-item .label {
            font-size: 9.5px;
            font-weight: 700;
            color: #64748b;
            text-transform: uppercase;
            letter-spacing: 0.4px;
          }
          .meta-item .val {
            font-size: 12px;
            font-weight: 700;
            color: #0f172a;
          }
          .meta-item .subval {
            font-size: 10.5px;
            color: #475569;
          }
          .accounts-box {
            background: #f8fafc;
            border: 1px solid #cbd5e1;
            border-radius: 6px;
            padding: 10px 12px;
            margin-bottom: 14px;
          }
          .accounts-title {
            font-size: 10px;
            font-weight: 800;
            color: #0f172a;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin-bottom: 8px;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
          .accounts-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11.5px;
          }
          .accounts-table th {
            background: #f1f5f9;
            color: #334155;
            font-size: 9.5px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.4px;
            padding: 6px 8px;
            border-top: 1px solid #cbd5e1;
            border-bottom: 1px solid #cbd5e1;
            text-align: left;
          }
          .accounts-table th.center, .accounts-table td.center {
            text-align: center;
          }
          .accounts-table th.right, .accounts-table td.right {
            text-align: right;
          }
          .accounts-table td {
            padding: 7px 8px;
            border-bottom: 1px solid #e2e8f0;
            vertical-align: middle;
          }
          .bills-badge {
            display: inline-block;
            background: #ecfdf5;
            color: #047857;
            border: 1px solid #a7f3d0;
            font-weight: 800;
            font-size: 10.5px;
            padding: 2px 7px;
            border-radius: 4px;
          }
          .accounts-total-row td {
            background: #f1f5f9;
            border-top: 1.5px solid #0f172a;
            font-weight: 800;
            color: #0f172a;
            padding: 7px 8px;
          }
          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 14px;
          }
          .items-table th {
            background: #f1f5f9;
            color: #334155;
            font-size: 10px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            padding: 7px 10px;
            border-top: 1px solid #cbd5e1;
            border-bottom: 1px solid #cbd5e1;
            text-align: left;
          }
          .items-table th.right, .items-table td.right {
            text-align: right;
          }
          .items-table td {
            padding: 8px 10px;
            border-bottom: 1px solid #f1f5f9;
            vertical-align: top;
          }
          .item-label {
            font-weight: 700;
            color: #0f172a;
            font-size: 11.5px;
          }
          .item-sub {
            font-size: 10px;
            color: #059669;
            margin-top: 1px;
            font-weight: 500;
          }
          .item-note {
            font-size: 10px;
            color: #0284c7;
            margin-top: 1px;
            font-weight: 500;
          }
          .amount-val {
            font-family: 'JetBrains Mono', monospace;
            font-weight: 700;
            font-size: 12px;
            color: #0f172a;
          }
          .total-row td {
            background: #f8fafc;
            border-top: 1.5px solid #0f172a;
            border-bottom: 1.5px solid #0f172a;
            padding: 9px 10px;
          }
          .total-row .total-label {
            font-weight: 800;
            font-size: 11.5px;
            color: #0f172a;
            text-transform: uppercase;
          }
          .total-row .total-amount {
            font-family: 'JetBrains Mono', monospace;
            font-weight: 900;
            font-size: 14px;
            color: #0f172a;
          }
          .summary-card {
            background: #f8fafc;
            border: 1px dashed #cbd5e1;
            border-radius: 6px;
            padding: 9px 12px;
            margin-bottom: 16px;
          }
          .summary-title {
            font-size: 9.5px;
            font-weight: 800;
            color: #475569;
            text-transform: uppercase;
            letter-spacing: 0.4px;
            margin-bottom: 5px;
          }
          .summary-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 3px 14px;
            font-size: 10.5px;
          }
          .summary-row {
            display: flex;
            justify-content: space-between;
            color: #475569;
          }
          .summary-row.bold {
            font-weight: 700;
            color: #0f172a;
          }
          .footer-note {
            font-size: 9.5px;
            color: #64748b;
            font-style: italic;
            margin-bottom: 18px;
            line-height: 1.35;
          }
          .signatures {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 28px;
            margin-top: 18px;
          }
          .sig-box {
            text-align: center;
          }
          .sig-line {
            border-top: 1px solid #94a3b8;
            padding-top: 4px;
            font-size: 10px;
            font-weight: 700;
            color: #334155;
          }
          .sig-role {
            font-size: 9px;
            color: #64748b;
          }
          @media print {
            body { padding: 0; background: none; }
            .slip-container { border: none; padding: 0; max-width: 100%; }
          }
        </style>
      </head>
      <body>
        <div class="slip-container">
          <div class="header">
            <div class="lgu-title">Republic of the Philippines &bull; Province of Romblon</div>
            <div class="lgu-name">MUNICIPALITY OF CONCEPCION</div>
            <div class="office-name">Office of the Municipal Treasurer &bull; Waterworks System</div>
            <div class="badge">Official Receipt (Form 25-A) Preparation Slip</div>
          </div>

          <div class="meta-grid">
            <div class="meta-item">
              <div class="label">Payor / Consumer:</div>
              <div class="val">${isMulti ? `Combined Accounts (${custBreakdowns.length > 0 ? custBreakdowns.length : customer.length} Accounts)` : primaryCustomer.name}</div>
              <div class="subval">${isMulti ? (custBreakdowns.map(c => c.name).join(', ')) : (primaryCustomer.barangay || 'Concepcion, Romblon')}</div>
            </div>
            <div class="meta-item">
              <div class="label">${isMulti ? 'Total Bills Selected:' : 'Account & Meter No:'}</div>
              <div class="val">${isMulti ? `${breakdown.selectedBillsCount || (custBreakdowns.reduce((sum, cb) => sum + cb.billsCount, 0))} Total Bills Selected` : `#${primaryCustomer.accountnumber || primaryCustomer.customerid}`}</div>
              <div class="subval">${isMulti ? `Across ${custBreakdowns.length > 0 ? custBreakdowns.length : customer.length} consumer accounts` : `Meter: ${primaryCustomer.meternumber || 'N/A'}`}</div>
            </div>
            <div class="meta-item">
              <div class="label">Date & Time:</div>
              <div class="val">${currentDate}</div>
            </div>
            <div class="meta-item">
              <div class="label">Collecting Officer / Cashier:</div>
              <div class="val">${cashierName}</div>
            </div>
          </div>

          ${isMulti && custBreakdowns.length > 0 ? `
          <div class="accounts-box">
            <div class="accounts-title">
              <span>Customer Accounts & Total Bills Summary</span>
              <span style="color: #047857; font-weight: 700;">${custBreakdowns.length} Accounts</span>
            </div>
            <table class="accounts-table">
              <thead>
                <tr>
                  <th style="width: 24px;">#</th>
                  <th>Customer Name</th>
                  <th>Account No.</th>
                  <th>Barangay</th>
                  <th class="center" style="width: 85px;">Total Bills</th>
                  <th>Billing Period</th>
                  <th class="right" style="width: 105px;">Amount to Pay</th>
                </tr>
              </thead>
              <tbody>
                ${custBreakdowns.map((cb, idx) => `
                  <tr>
                    <td style="color: #64748b; font-weight: 700;">${idx + 1}</td>
                    <td><strong>${cb.name}</strong></td>
                    <td style="font-family: monospace; color: #475569;">#${cb.accountno}</td>
                    <td style="color: #334155;">${cb.barangay || 'Concepcion'}</td>
                    <td class="center">
                      <span class="bills-badge">${cb.billsCount} bill${cb.billsCount > 1 ? 's' : ''}</span>
                    </td>
                    <td style="font-size: 11px; color: #334155;">${cb.monthRange || 'N/A'}</td>
                    <td class="right amount-val">
                      ${formatCurrency(cb.effectivePayment)}
                      ${cb.excessPayment > 0 ? `<div style="font-size: 9px; color: #15803d; font-weight: 600;">(incl. ${formatCurrency(cb.excessPayment)} adv)</div>` : ''}
                    </td>
                  </tr>
                `).join('')}
                <tr class="accounts-total-row">
                  <td colspan="4"><strong>TOTAL COMBINED ACCOUNTS</strong></td>
                  <td class="center">
                    <span class="bills-badge" style="background: #047857; color: #ffffff; border-color: #047857;">
                      ${breakdown.selectedBillsCount || custBreakdowns.reduce((sum, cb) => sum + cb.billsCount, 0)} BILLS
                    </span>
                  </td>
                  <td></td>
                  <td class="right amount-val"><strong>${formatCurrency(breakdown.totalReceiptAmount)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
          ` : ''}

          <table class="items-table">
            <thead>
              <tr>
                <th style="width: 32px;">#</th>
                <th>Nature of Collection / Particulars (to write on OR)</th>
                <th class="right" style="width: 120px;">Amount</th>
              </tr>
            </thead>
            <tbody>
              ${breakdown.lineItems.map((item, idx) => `
                <tr>
                  <td style="color: #64748b; font-weight: 700;">${idx + 1}</td>
                  <td>
                    <div class="item-label">${item.label}</div>
                    ${item.deductionNote ? `<div class="item-sub">${item.deductionNote}</div>` : ''}
                    ${item.note ? `<div class="item-note">${item.note}</div>` : ''}
                  </td>
                  <td class="right amount-val">${formatCurrency(item.amount)}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="2" class="total-label">Total Amount to be Written on Official Receipt</td>
                <td class="right total-amount">${formatCurrency(breakdown.totalReceiptAmount)}</td>
              </tr>
            </tbody>
          </table>

          <div class="summary-card">
            <div class="summary-title">Accounting Reconciliation Breakdown:</div>
            <div class="summary-grid">
              <div class="summary-row">
                <span>Total Basic Charges (${breakdown.selectedBillsCount || 0} bill/s):</span>
                <strong>${formatCurrency(breakdown.totalBasic)}</strong>
              </div>
              <div class="summary-row">
                <span>Total Discounts Applied:</span>
                <strong style="color: #059669;">-${formatCurrency(breakdown.totalDiscount || 0)}</strong>
              </div>
              <div class="summary-row">
                <span>Active Credit Deducted:</span>
                <strong style="color: #0284c7;">-${formatCurrency(breakdown.totalCreditApplied || 0)}</strong>
              </div>
              <div class="summary-row">
                <span>Total Surcharges:</span>
                <strong style="color: #dc2626;">+${formatCurrency(breakdown.totalSurcharge || 0)}</strong>
              </div>
            </div>
          </div>

          <div class="footer-note">
            * This slip serves as the itemized guide for transcribing billing lines onto General Form No. 25-A (Official Receipt).
          </div>

          <div class="signatures">
            <div class="sig-box">
              <div class="sig-line">Payor / Representative</div>
              <div class="sig-role">Signature over Printed Name</div>
            </div>
            <div class="sig-box">
              <div class="sig-line">${cashierName}</div>
              <div class="sig-role">Collecting Officer / Cashier</div>
            </div>
          </div>
        </div>

        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handlePrintSelectedBills = () => {
    const summary = getGlobalSelectedSummary();
    if (!summary || summary.totalBillsCount === 0) {
      setError('No bills selected for printing');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const { allSelectedBills, customers, totalBasic, totalSurcharge, totalDiscount, totalCreditApplied, netAmountPayable } = summary;
    const isMulti = customers.length > 1;
    const customerTitle = isMulti ? `Combined Accounts (${customers.length} Consumers)` : (customers[0]?.name || 'Consumer');

    const currentDate = new Date().toLocaleDateString('en-PH', { 
      year: 'numeric', month: 'long', day: 'numeric', 
      hour: '2-digit', minute: '2-digit' 
    });

    const openFn = typeof globalThis.open === 'function' ? globalThis.open : window.open;
    let printWindow = null;
    try {
      printWindow = openFn('', '', 'width=900,height=700');
    } catch (e) {
      console.error('Error opening print window:', e);
      printWindow = null;
    }

    if (!printWindow || !printWindow.document) {
      console.error('Print window or document not available');
      setError('Unable to open print window. Please allow pop-ups and try again.');
      return;
    }
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Billing Statement - ${customerTitle}</title>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
        <style>
          :root {
            --primary-color: #0f172a;
            --secondary-color: #64748b;
            --accent-color: #0ea5e9;
            --danger-color: #ef4444;
            --success-color: #10b981;
            --border-color: #e2e8f0;
            --bg-subtle: #f8fafc;
          }

          body {
            font-family: 'Inter', -apple-system, sans-serif;
            color: var(--primary-color);
            margin: 0;
            padding: 32px;
            background-color: #ffffff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .header-container {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            border-bottom: 2px solid var(--primary-color);
            padding-bottom: 20px;
            margin-bottom: 24px;
          }

          .header-left h1 {
            font-size: 20px;
            font-weight: 800;
            letter-spacing: -0.5px;
            margin: 0 0 4px 0;
            text-transform: uppercase;
          }

          .header-left p {
            margin: 0;
            color: var(--secondary-color);
            font-size: 13px;
          }

          .document-title {
            text-align: right;
          }

          .document-title h2 {
            margin: 0;
            font-size: 18px;
            font-weight: 600;
            color: var(--secondary-color);
            text-transform: uppercase;
            letter-spacing: 1px;
          }

          .document-title p {
            margin: 4px 0 0;
            font-size: 13px;
            color: var(--secondary-color);
          }

          .customer-section {
            background-color: var(--bg-subtle);
            border-radius: 8px;
            padding: 16px 20px;
            margin-bottom: 24px;
            border: 1px solid var(--border-color);
          }

          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 24px;
          }

          th {
            background-color: var(--bg-subtle);
            color: var(--secondary-color);
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            padding: 10px 12px;
            text-align: left;
            border-bottom: 1px solid #cbd5e1;
          }

          td {
            padding: 10px 12px;
            border-bottom: 1px solid var(--border-color);
            font-size: 13px;
          }

          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .amount-cell { font-family: monospace; font-size: 14px; font-weight: 600; }
          
          .tag {
            display: inline-block;
            padding: 2px 6px;
            border-radius: 4px;
            font-size: 10px;
            font-weight: 600;
            text-transform: uppercase;
          }

          .tag-red { background-color: #fee2e2; color: var(--danger-color); }
          .tag-green { background-color: #d1fae5; color: var(--success-color); }

          .credit-note {
            font-size: 11px;
            color: var(--accent-color);
            font-family: 'Inter', sans-serif;
            font-weight: 500;
          }

          .summary-section {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 32px;
          }

          .summary-table {
            width: 320px;
            background-color: var(--bg-subtle);
            border: 1px dashed #cbd5e1;
            border-radius: 8px;
            padding: 16px;
          }

          .summary-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 6px;
            font-size: 13px;
            color: var(--secondary-color);
          }

          .summary-row.total {
            border-top: 1px solid var(--primary-color);
            margin-top: 8px;
            padding-top: 8px;
            font-weight: 800;
            font-size: 16px;
            color: var(--primary-color);
          }

          .footer-notes {
            font-size: 12px;
            color: var(--secondary-color);
            border-top: 1px solid var(--border-color);
            padding-top: 16px;
            margin-top: 24px;
          }

          @media print {
            body { padding: 0; }
          }
        </style>
      </head>
      <body>
        <div class="header-container">
          <div class="header-left">
            <h1>Municipality of Concepcion</h1>
            <p>Office of the Municipal Treasurer • Waterworks Operation</p>
          </div>
          <div class="document-title">
            <h2>Statement of Account</h2>
            <p>Generated: ${currentDate}</p>
          </div>
        </div>

        <div class="customer-section">
          <div style="font-size: 14px; font-weight: 700; margin-bottom: 4px;">
            ${isMulti ? `Combined Accounts (${customers.length} Consumers)` : customers[0]?.name}
          </div>
          <div style="color: var(--secondary-color); font-size: 12px;">
            ${customers.map(c => `${c.name} (#${c.accountnumber || c.customerid})`).join(' &bull; ')}
          </div>
        </div>

        <table>
          <thead>
            <tr>
              ${isMulti ? '<th>Consumer</th>' : ''}
              <th>Bill ID</th>
              <th>Billing Period</th>
              <th class="text-right">Consumption</th>
              <th class="text-right">Basic Amount</th>
              <th class="text-center">Adjustments</th>
              <th class="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            ${allSelectedBills.map(bill => {
              const surAmount = bill.surchargeInfo?.surchargeAmount || bill.surchargeamount || 0;
              const discAmount = bill.calculatedDiscountAmount || bill.discountamount || 0;
              
              let adjustments = [];
              if (surAmount > 0) adjustments.push(`<span class="tag tag-red">Sur: ${formatCurrency(surAmount)}</span>`);
              if (discAmount > 0) adjustments.push(`<span class="tag tag-green">Disc: ${formatCurrency(discAmount)}</span>`);
              
              return `
              <tr>
                ${isMulti ? `<td><strong>${bill.customer?.name || 'N/A'}</strong></td>` : ''}
                <td>#${bill.billid}</td>
                <td>${formatMonth(bill.billedmonth)}</td>
                <td class="text-right">${bill.consumption || '0'} m³</td>
                <td class="text-right amount-cell">
                  ${formatCurrency(bill.basicamount)}
                  ${bill.creditApplied > 0 ? `<div class="credit-note">-${formatCurrency(bill.creditApplied)} Credit</div>` : ''}
                </td>
                <td class="text-center">${adjustments.length > 0 ? adjustments.join(' ') : '<span style="color: #cbd5e1">—</span>'}</td>
                <td class="text-right amount-cell">${formatCurrency(bill.netTotal !== undefined ? bill.netTotal : (bill.calculatedTotal || bill.totalbillamount))}</td>
              </tr>
              `;
            }).join('')}
          </tbody>
        </table>

        <div class="summary-section">
          <div class="summary-table">
            <div class="summary-row">
              <span>Subtotal (Basic)</span>
              <span>${formatCurrency(totalBasic)}</span>
            </div>
            ${totalSurcharge > 0 ? `
            <div class="summary-row">
              <span>Total Surcharge</span>
              <span style="color: var(--danger-color)">+${formatCurrency(totalSurcharge)}</span>
            </div>` : ''}
            ${totalDiscount > 0 ? `
            <div class="summary-row">
              <span>Total Discount</span>
              <span style="color: var(--success-color)">-${formatCurrency(totalDiscount)}</span>
            </div>` : ''}
            ${totalCreditApplied > 0 ? `
            <div class="summary-row">
              <span>Credit Applied</span>
              <span style="color: var(--accent-color)">-${formatCurrency(totalCreditApplied)}</span>
            </div>` : ''}
            <div class="summary-row total">
              <span>Net Amount Due</span>
              <span style="color: var(--success-color)">${formatCurrency(netAmountPayable)}</span>
            </div>
          </div>
        </div>

        <div class="footer-notes">
          <p>Please present this Statement of Account when transacting at the Municipal Treasurer's Office.</p>
        </div>

        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="h1" fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon sx={{ color: '#059669' }} /> Cashier Payment & Collection
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Receive water payments, allocate customer credits, calculate automatic surcharges/discounts, and print official statements.
        </Typography>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={4}>
            <Card sx={{ p: 2, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 4px 16px -2px rgba(0, 0, 0, 0.04)' }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Today's Cash Collection</Typography>
              <Typography variant="h5" fontWeight={800} color="#059669" sx={{ mt: 0.5 }}>{formatCurrency(stats.todayCollection)}</Typography>
              <Typography variant="caption" color="text.secondary">{stats.todayCount} transactions settled today</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ p: 2, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 4px 16px -2px rgba(0, 0, 0, 0.04)' }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Month's Total Collection</Typography>
              <Typography variant="h5" fontWeight={800} color="#0284c7" sx={{ mt: 0.5 }}>{formatCurrency(stats.monthCollection)}</Typography>
              <Typography variant="caption" color="text.secondary">{stats.monthCount} monthly receipts</Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ p: 2, borderRadius: 3, border: '1px solid #e2e8f0', bgcolor: '#ffffff', boxShadow: '0 4px 16px -2px rgba(0, 0, 0, 0.04)' }}>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Recorded Payments</Typography>
              <Typography variant="h5" fontWeight={800} color="#d97706" sx={{ mt: 0.5 }}>{stats.todayCount + stats.monthCount}</Typography>
              <Typography variant="caption" color="text.secondary">System transactions verified</Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Alerts */}
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, borderRadius: 2.5 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2, borderRadius: 2.5 }}>{success}</Alert>}

      {/* Modern Tabs */}
      <Box sx={{ borderBottom: '1px solid #e2e8f0', mb: 3 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)} 
          variant="scrollable" 
          scrollButtons="auto"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: '#059669',
              height: 3,
              borderRadius: '3px 3px 0 0'
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              minHeight: 48,
              '&.Mui-selected': {
                color: '#059669'
              }
            }
          }}
        >
          <Tab icon={<SearchIcon />} iconPosition="start" label="Consumer Search" value="search" />
          {customerTabs.map(tab => {
            const selectedCount = tab.selectedBills?.length || 0;
            return (
              <Tab 
                key={tab.customer.customerid} 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <span>{tab.customer.name}</span>
                    {selectedCount > 0 && (
                      <Chip
                        label={selectedCount}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.68rem',
                          fontWeight: 700,
                          bgcolor: '#059669',
                          color: '#ffffff',
                          px: 0.25
                        }}
                      />
                    )}
                    <Box 
                      component="span"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCloseTab(tab.customer.customerid);
                      }}
                      sx={{ 
                        p: 0.25, 
                        ml: 0.25, 
                        cursor: 'pointer', 
                        display: 'inline-flex',
                        alignItems: 'center',
                        borderRadius: '50%',
                        color: '#94a3b8',
                        '&:hover': { bgcolor: '#fee2e2', color: '#dc2626' }
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </Box>
                  </Box>
                } 
                value={tab.customer.customerid} 
              />
            );
          })}
        </Tabs>
      </Box>

      {/* Search Tab */}
      {activeTab === 'search' && (
        <Box>
          <Card sx={{ mb: 3, p: 2.5, borderRadius: 3.5, border: '1px solid #e2e8f0', boxShadow: '0 4px 16px -2px rgba(0, 0, 0, 0.04)' }}>
            <Grid container spacing={1.5} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  placeholder="Search consumer name, meter or account ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Button variant="contained" size="small" onClick={handleSearch} disabled={loading} sx={{ borderRadius: 2, bgcolor: '#059669', '&:hover': { bgcolor: '#047857' } }}>
                          Search
                        </Button>
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2.5 }
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <FormControl fullWidth size="small">
                  <Select value={filterBarangay} onChange={(e) => setFilterBarangay(e.target.value)} sx={{ borderRadius: 2.5 }}>
                    <MenuItem value="all">All Barangays</MenuItem>
                    {barangays.map(b => <MenuItem key={b.barangay} value={b.barangay}>{b.barangay}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <FormControl fullWidth size="small">
                  <Select value={filterType} onChange={(e) => setFilterType(e.target.value)} sx={{ borderRadius: 2.5 }}>
                    <MenuItem value="all">All Classifications</MenuItem>
                    {customerTypes.map(t => <MenuItem key={t.type} value={t.type}>{t.type}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Button variant="outlined" fullWidth onClick={handleBrowseAll} disabled={loading} sx={{ borderRadius: 2.5, borderColor: '#e2e8f0', color: '#475569' }}>
                  Browse All
                </Button>
              </Grid>
            </Grid>
          </Card>

          {searchResults.length > 0 && (
            <Card sx={{ mb: 3, borderRadius: 3.5, border: '1px solid #e2e8f0', boxShadow: '0 4px 16px -2px rgba(0, 0, 0, 0.04)', overflow: 'hidden' }}>
              <Box sx={{ p: 2.5, bgcolor: '#f8faf9', borderBottom: '1px solid #e2e8f0' }}>
                <Typography variant="subtitle1" fontWeight={700} color="#0f172a">Matching Consumer Accounts ({searchResults.length})</Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f8faf9' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Account ID</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Consumer Name</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Classification</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Barangay</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569' }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchResults.map(customer => (
                      <TableRow key={customer.customerid} hover onClick={() => handleSelectCustomer(customer)} sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8faf9' } }}>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#0f172a' }}>{customer.customerid}</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#0f172a' }}>{customer.name}</TableCell>
                        <TableCell><Chip label={customer.type} size="small" sx={{ bgcolor: '#f1f5f9', color: '#334155', fontWeight: 600, fontSize: '0.72rem' }} /></TableCell>
                        <TableCell sx={{ color: '#475569' }}>{customer.barangay}</TableCell>
                        <TableCell>
                          <Chip 
                            label={customer.status} 
                            size="small"
                            sx={{ 
                              bgcolor: customer.status === 'Active' ? '#ecfdf5' : '#fee2e2',
                              color: customer.status === 'Active' ? '#047857' : '#b91c1c',
                              fontWeight: 700
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" variant="contained" onClick={(e) => { e.stopPropagation(); handleSelectCustomer(customer); }} sx={{ borderRadius: 2, bgcolor: '#059669', textTransform: 'none', '&:hover': { bgcolor: '#047857' } }}>
                              Open Cashier
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AddIcon />}
                              onClick={(e) => { e.stopPropagation(); openAddModal(customer); }}
                              sx={{ borderRadius: 2, borderColor: '#e2e8f0', color: '#475569', textTransform: 'none' }}
                            >
                              Add Bill
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          )}

          {recentPayments.length > 0 && (() => {
            const combinedPayments = getCombinedRecentPayments(recentPayments);
            return (
              <Card sx={{ borderRadius: 3.5, border: '1px solid #e2e8f0', boxShadow: '0 4px 16px -2px rgba(0, 0, 0, 0.04)', overflow: 'hidden' }}>
                <Box sx={{ p: 2.5, bgcolor: '#f8faf9', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700} color="#0f172a">Recent Cashier Payments</Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={allPaymentsExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                    onClick={() => handleToggleExpandAllPayments(combinedPayments)}
                    sx={{
                      borderRadius: 2,
                      borderColor: '#cbd5e1',
                      color: '#475569',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      textTransform: 'none',
                      bgcolor: '#ffffff',
                      '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' }
                    }}
                  >
                    {allPaymentsExpanded ? 'Collapse All Payments' : 'Expand All Payments'}
                  </Button>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f8faf9' }}>
                      <TableRow>
                        <TableCell sx={{ width: 44 }} />
                        <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Date Paid</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Consumer</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Billed Month</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#475569' }} align="right">Amount Collected</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {combinedPayments.map(payment => {
                        const isExpanded = Boolean(expandedPaymentRows[payment.id]);
                        const isMultiBill = payment.bills.length > 1;

                        return (
                          <React.Fragment key={payment.id}>
                            <TableRow 
                              hover 
                              onClick={() => isMultiBill ? toggleExpandPaymentRow(payment.id) : handleViewBillDetails(payment.billid, payment.customerid)} 
                              sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f8faf9' }, bgcolor: isExpanded ? '#f8faf9' : 'inherit' }}
                            >
                              <TableCell sx={{ p: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpandPaymentRow(payment.id);
                                  }}
                                  sx={{ color: '#64748b' }}
                                >
                                  {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                </IconButton>
                              </TableCell>
                              <TableCell sx={{ color: '#64748b', fontSize: '0.8125rem' }}>{formatDate(payment.datepaid)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={700} color="#0f172a">{payment.customer?.name || payment.customerid}</Typography>
                                <Typography variant="caption" color="text.secondary">ID: {payment.customerid}</Typography>
                              </TableCell>
                              <TableCell sx={{ color: '#475569' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="body2">{payment.billedMonthsFormatted}</Typography>
                                  {isMultiBill && (
                                    <Chip 
                                      label={`${payment.bills.length} Bills`} 
                                      size="small" 
                                      onClick={(e) => { e.stopPropagation(); toggleExpandPaymentRow(payment.id); }}
                                      sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 700, cursor: 'pointer' }} 
                                    />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="right" sx={{ color: '#059669', fontWeight: 800 }}>{formatCurrency(payment.totalbillamount)}</TableCell>
                            </TableRow>

                            {/* Sub-table Breakdown */}
                            <TableRow>
                              <TableCell colSpan={5} sx={{ p: 0, borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none' }}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ p: 2, bgcolor: '#f8faf9', borderLeft: '4px solid #059669', my: 1, mx: 2, borderRadius: 2 }}>
                                    <Typography variant="caption" fontWeight={700} color="#047857" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
                                      Payment Breakdown ({payment.bills.length} Bill{payment.bills.length > 1 ? 's' : ''})
                                    </Typography>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow sx={{ '& th': { fontSize: '0.72rem', fontWeight: 700, color: '#64748b', bgcolor: '#ffffff' } }}>
                                          <TableCell>Bill ID</TableCell>
                                          <TableCell>Billed Month</TableCell>
                                          <TableCell align="right">Bill Amount</TableCell>
                                          <TableCell align="right">Action</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {payment.bills.map(bill => (
                                          <TableRow key={bill.billid} sx={{ '&:last-child td': { border: 0 }, bgcolor: '#ffffff' }}>
                                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 700, color: '#0f172a' }}>{bill.billid}</TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem', color: '#334155' }}>{formatMonth(bill.billedmonth)}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#059669' }}>{formatCurrency(bill.totalbillamount)}</TableCell>
                                            <TableCell align="right">
                                              <Button 
                                                size="small" 
                                                variant="text" 
                                                onClick={() => handleViewBillDetails(bill.billid, bill.customerid)} 
                                                sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#059669', p: 0.5, minWidth: 0, textTransform: 'none' }}
                                              >
                                                View Receipt
                                              </Button>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            );
          })()}
        </Box>
      )}

      {/* Customer Tab Content */}
      {activeTab !== 'search' && getActiveCustomerTab() && (() => {
        const currentTab = getActiveCustomerTab();
        const { customer, unpaidBills, paymentHistory, billsWithSurcharge, selectedBills } = currentTab;
        
        return (
          <Stack spacing={3}>
            {/* Customer Info Card */}
            <Card sx={{ borderRadius: 3.5, border: '1px solid #e2e8f0', p: 2.5, boxShadow: '0 4px 16px -2px rgba(0, 0, 0, 0.04)' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 1 }}>
                    <Typography variant="h5" fontWeight={800} color="#0f172a" sx={{ lineHeight: 1.2 }}>
                      {customer.name}
                    </Typography>
                    <Chip 
                      label={`ID: ${customer.customerid}`} 
                      size="small" 
                      sx={{ borderRadius: 1.5, height: 24, fontSize: '0.75rem', fontFamily: 'monospace', fontWeight: 600 }} 
                    />
                    <Chip 
                      label={customer.status} 
                      size="small"
                      sx={{ 
                        height: 24, 
                        fontWeight: 700, 
                        fontSize: '0.75rem',
                        bgcolor: customer.status === 'Active' ? '#ecfdf5' : '#fee2e2',
                        color: customer.status === 'Active' ? '#047857' : '#b91c1c'
                      }}
                    />
                  </Box>
                  
                  <Stack direction="row" spacing={2} divider={<Divider orientation="vertical" flexItem />} sx={{ mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {customer.type || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {customer.barangay || 'N/A'} {customer.address ? `(${customer.address})` : ''}
                    </Typography>
                    {customer.discount && (
                      <Typography variant="body2" color="#059669" fontWeight={700}>
                        Discount: {customer.discount}%
                      </Typography>
                    )}
                  </Stack>
                  
                  {customer.remarks && (
                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#64748b', mt: 1 }}>
                      <strong style={{ fontStyle: 'normal', color: '#0f172a' }}>Remarks:</strong> {customer.remarks}
                    </Typography>
                  )}
                </Grid>

                {/* Right Side: Credit Balance */}
                <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, alignItems: 'center' }}>
                  <Box sx={{ textAlign: { xs: 'left', md: 'right' }, p: 2, bgcolor: '#f8faf9', borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>AVAILABLE CREDIT BALANCE</Typography>
                    <Typography variant="h4" color="#059669" fontWeight={800}>
                      {formatCurrency(currentTab.creditBalance)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Card>

            {/* Unpaid Bills */}
            <Card sx={{ borderRadius: 3.5, border: '1px solid #e2e8f0', boxShadow: '0 4px 16px -2px rgba(0, 0, 0, 0.04)', overflow: 'hidden' }}>
              <Box sx={{ p: 2.5, bgcolor: '#f8faf9', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700} color="#0f172a">
                    Unpaid Water Bills 
                  </Typography>
                  {unpaidBills.length > 0 && <Chip label={`${unpaidBills.length} Pending`} size="small" sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 700 }} />}
                </Box>
                <Stack direction="row" spacing={1.5}>
                  {selectedBills.length === 0 && unpaidBills.length > 0 && (
                    <Button 
                      variant="outlined" 
                      size="small" 
                      onClick={() => setShowAutoSelectDialog(true)}
                      startIcon={<PaymentIcon />}
                      sx={{ borderRadius: 2, borderColor: '#e2e8f0', color: '#475569', fontWeight: 600, textTransform: 'none' }}
                    >
                      Auto-Select by Amount
                    </Button>
                  )}
                  <Button 
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => openAddModal(customer)}
                    sx={{ borderRadius: 2, bgcolor: '#059669', color: '#ffffff', fontWeight: 600, textTransform: 'none', '&:hover': { bgcolor: '#047857' } }}
                  >
                    Add Bill
                  </Button>
                </Stack>
              </Box>
              
              {unpaidBills.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#ffffff' }}>
                  <CheckCircleIcon sx={{ color: '#059669', fontSize: 40, mb: 1 }} />
                  <Typography variant="body1" fontWeight={600} color="#0f172a">No outstanding bills for this consumer!</Typography>
                  <Typography variant="caption" color="text.secondary">All previous billings have been settled in full.</Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f8faf9' }}>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox 
                            checked={selectedBills.length === billsWithSurcharge.length && billsWithSurcharge.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) updateSelectedBills(billsWithSurcharge.map(b => b.billid));
                              else updateSelectedBills([]);
                            }}
                            sx={{ color: '#059669', '&.Mui-checked': { color: '#059669' } }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Bill ID</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Month</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#475569' }}>Prev</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#475569' }}>Curr</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#475569' }}>Cons.</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#475569' }}>Basic</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, color: '#475569' }}>Adjustments</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: '#475569' }}>Net Total</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {billsWithSurcharge.map(bill => {
                        const isSelected = selectedBills.includes(bill.billid);
                        return (
                          <TableRow 
                            key={bill.billid} 
                            hover 
                            selected={isSelected}
                            onClick={() => {
                              if (isSelected) {
                                updateSelectedBills(selectedBills.filter(id => id !== bill.billid));
                              } else {
                                updateSelectedBills([...selectedBills, bill.billid]);
                              }
                            }}
                            sx={{ 
                              cursor: 'pointer',
                              '&.Mui-selected': { bgcolor: '#ecfdf5 !important' },
                              '&:hover': { bgcolor: isSelected ? '#d1fae5 !important' : '#f8faf9' }
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox 
                                checked={isSelected}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  if (e.target.checked) updateSelectedBills([...selectedBills, bill.billid]);
                                  else updateSelectedBills(selectedBills.filter(id => id !== bill.billid));
                                }}
                                sx={{ color: '#059669', '&.Mui-checked': { color: '#059669' } }}
                              />
                            </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#0f172a' }}>{bill.billid}</TableCell>
                          <TableCell sx={{ color: '#475569', fontWeight: 600 }}>
                            {formatMonth(bill.billedmonth)}
                            {bill.surchargeInfo?.daysOverdue > 0 && (
                              <Typography variant="caption" display="block" color="#dc2626" sx={{ fontSize: '0.7rem', fontWeight: 700 }}>
                                {bill.surchargeInfo.daysOverdue} days overdue
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#64748b' }}>{bill.previousreading}</TableCell>
                          <TableCell align="right" sx={{ color: '#64748b' }}>{bill.currentreading}</TableCell>
                          <TableCell align="right" sx={{ color: '#0f172a', fontWeight: 600 }}>{bill.consumption} m³</TableCell>
                          <TableCell align="right" sx={{ color: '#0f172a' }}>
                            {formatCurrency(bill.basicamount)}
                            {bill.creditApplied > 0 && (
                              <Typography variant="caption" display="block" color="#059669" sx={{ fontSize: '0.7rem', fontWeight: 700 }}>
                                - {formatCurrency(bill.creditApplied)} Credit
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              {bill.surchargeInfo?.surchargeAmount > 0 && (
                                <Chip label={`+${formatCurrency(bill.surchargeInfo.surchargeAmount)} Sur`} size="small" sx={{ bgcolor: '#fee2e2', color: '#b91c1c', fontWeight: 700, height: 20, fontSize: '0.65rem' }} />
                              )}
                              {bill.calculatedDiscountAmount > 0 && (
                                <Chip label={`-${formatCurrency(bill.calculatedDiscountAmount)} Disc`} size="small" sx={{ bgcolor: '#ecfdf5', color: '#047857', fontWeight: 700, height: 20, fontSize: '0.65rem' }} />
                              )}
                              {!bill.surchargeInfo?.surchargeAmount && !bill.calculatedDiscountAmount && (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800, color: '#0f172a' }}>
                            {formatCurrency(bill.netTotal !== undefined ? bill.netTotal : (bill.calculatedTotal || bill.totalbillamount))}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={bill.paymentstatus} 
                              size="small" 
                              sx={{
                                bgcolor: bill.paymentstatus === 'Unpaid' ? '#fee2e2' : '#fef3c7',
                                color: bill.paymentstatus === 'Unpaid' ? '#b91c1c' : '#92400e',
                                fontWeight: 700,
                                fontSize: '0.72rem'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>

            {/* Payment History */}
            <Card sx={{ borderRadius: 3.5, border: '1px solid #e2e8f0', boxShadow: '0 4px 16px -2px rgba(0, 0, 0, 0.04)', overflow: 'hidden' }}>
              {(() => {
                const combinedHistory = getCombinedPaymentHistory(paymentHistory);
                const sorted = getSortedHistory(combinedHistory);
                const start = historyPage * historyRowsPerPage;
                const slice = sorted.slice(start, start + historyRowsPerPage);

                return (
                  <>
                    <Box sx={{ p: 2.5, bgcolor: '#f8faf9', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700} color="#0f172a">Payment History & Receipts</Typography>
                      {combinedHistory.length > 0 && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={allHistoryExpanded ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
                          onClick={() => handleToggleExpandAllHistory(combinedHistory)}
                          sx={{
                            borderRadius: 2,
                            borderColor: '#cbd5e1',
                            color: '#475569',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            textTransform: 'none',
                            bgcolor: '#ffffff',
                            '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' }
                          }}
                        >
                          {allHistoryExpanded ? 'Collapse All History' : 'Expand All History'}
                        </Button>
                      )}
                    </Box>
                    <CardContent sx={{ p: 0 }}>
                      {paymentHistory.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                          <Typography color="#64748b">No historical payments on record for this account</Typography>
                        </Box>
                      ) : (
                        <TableContainer>
                          <Table size="small">
                            <TableHead sx={{ bgcolor: '#f8faf9' }}>
                              <TableRow>
                                <TableCell sx={{ width: 44 }} />
                                <TableCell sx={{ fontWeight: 700, color: '#475569' }}>
                                  <TableSortLabel 
                                    active={historySortConfig.key === 'billedmonth'} 
                                    direction={historySortConfig.key === 'billedmonth' ? historySortConfig.direction : 'asc'}
                                    onClick={() => handleHistorySort('billedmonth')}
                                  >
                                    Period
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: '#475569' }}>Prev</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: '#475569' }}>Curr</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: '#475569' }}>
                                  <TableSortLabel 
                                    active={historySortConfig.key === 'consumption'} 
                                    direction={historySortConfig.key === 'consumption' ? historySortConfig.direction : 'asc'}
                                    onClick={() => handleHistorySort('consumption')}
                                  >
                                    Cons.
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569' }}>
                                  <TableSortLabel 
                                    active={historySortConfig.key === 'datepaid'} 
                                    direction={historySortConfig.key === 'datepaid' ? historySortConfig.direction : 'asc'}
                                    onClick={() => handleHistorySort('datepaid')}
                                  >
                                    Date Paid
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: '#475569' }}>
                                  <TableSortLabel 
                                    active={historySortConfig.key === 'amount'} 
                                    direction={historySortConfig.key === 'amount' ? historySortConfig.direction : 'asc'}
                                    onClick={() => handleHistorySort('amount')}
                                  >
                                    Amount
                                  </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Action</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {slice.map(group => {
                                const isExpanded = Boolean(expandedHistoryRows[group.id]);
                                const isMultiBill = group.bills.length > 1;

                                return (
                                  <React.Fragment key={group.id}>
                                    <TableRow 
                                      hover 
                                      onClick={() => isMultiBill ? toggleExpandHistoryRow(group.id) : handleViewBillDetails(group.billid, customer.customerid)} 
                                      sx={{ 
                                        cursor: 'pointer',
                                        '&:hover': { bgcolor: '#f8faf9' },
                                        bgcolor: isExpanded ? '#f8faf9' : 'inherit'
                                      }}
                                    >
                                      <TableCell sx={{ p: 0.5 }}>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleExpandHistoryRow(group.id);
                                          }}
                                          sx={{ color: '#64748b' }}
                                        >
                                          {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                        </IconButton>
                                      </TableCell>
                                      <TableCell sx={{ fontWeight: 600, color: '#0f172a' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Typography variant="body2" fontWeight={600}>{group.billedmonth}</Typography>
                                          {isMultiBill && (
                                            <Chip 
                                              label={`${group.bills.length} Bills`} 
                                              size="small" 
                                              onClick={(e) => { e.stopPropagation(); toggleExpandHistoryRow(group.id); }}
                                              sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 700, cursor: 'pointer' }} 
                                            />
                                          )}
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right" sx={{ color: '#64748b' }}>{isMultiBill ? '-' : group.previousreading}</TableCell>
                                      <TableCell align="right" sx={{ color: '#64748b' }}>{isMultiBill ? '-' : group.currentreading}</TableCell>
                                      <TableCell align="right" sx={{ color: '#0f172a', fontWeight: 600 }}>{group.consumption} m³</TableCell>
                                      <TableCell sx={{ color: '#64748b', fontSize: '0.8125rem' }}>{formatDate(group.datepaid)}</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 800, color: '#059669' }}>{formatCurrency(group.totalbillamount)}</TableCell>
                                      <TableCell>
                                        <Chip 
                                          label={group.paymentstatus} 
                                          size="small" 
                                          sx={{
                                            bgcolor: group.paymentstatus === 'Paid' ? '#ecfdf5' : '#fee2e2',
                                            color: group.paymentstatus === 'Paid' ? '#047857' : '#b91c1c',
                                            fontWeight: 700,
                                            fontSize: '0.72rem'
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Stack direction="row" spacing={0.5}>
                                          <Tooltip title="View Receipt Details">
                                            <IconButton 
                                              size="small" 
                                              sx={{ color: '#059669' }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewBillDetails(group.billid, customer.customerid);
                                              }}
                                            >
                                              <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          {!isMultiBill && (
                                            <Tooltip title="Mark as Unpaid">
                                              <IconButton 
                                                size="small" 
                                                sx={{ color: '#d97706' }}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setBillToMarkUnpaid(group.bills[0]);
                                                  setShowUnpaidConfirm(true);
                                                }}
                                              >
                                                <UndoIcon fontSize="small" />
                                              </IconButton>
                                            </Tooltip>
                                          )}
                                        </Stack>
                                      </TableCell>
                                    </TableRow>

                                    {/* Sub-table Breakdown for Multi-bill History */}
                                    <TableRow>
                                      <TableCell colSpan={9} sx={{ p: 0, borderBottom: isExpanded ? '1px solid #e2e8f0' : 'none' }}>
                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                          <Box sx={{ p: 2, bgcolor: '#f8faf9', borderLeft: '4px solid #059669', my: 1, mx: 2, borderRadius: 2 }}>
                                            <Typography variant="caption" fontWeight={700} color="#047857" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
                                              Payment History Breakdown ({group.bills.length} Bill{group.bills.length > 1 ? 's' : ''})
                                            </Typography>
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow sx={{ '& th': { fontSize: '0.72rem', fontWeight: 700, color: '#64748b', bgcolor: '#ffffff' } }}>
                                                  <TableCell>Bill ID</TableCell>
                                                  <TableCell>Period</TableCell>
                                                  <TableCell align="right">Prev</TableCell>
                                                  <TableCell align="right">Curr</TableCell>
                                                  <TableCell align="right">Cons.</TableCell>
                                                  <TableCell align="right">Amount</TableCell>
                                                  <TableCell align="right">Actions</TableCell>
                                                </TableRow>
                                              </TableHead>
                                              <TableBody>
                                                {group.bills.map(bill => (
                                                  <TableRow key={bill.billid} sx={{ '&:last-child td': { border: 0 }, bgcolor: '#ffffff' }}>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 700, color: '#0f172a' }}>{bill.billid}</TableCell>
                                                    <TableCell sx={{ fontSize: '0.75rem', color: '#334155', fontWeight: 600 }}>{formatMonth(bill.billedmonth)}</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.75rem', color: '#64748b' }}>{bill.previousreading}</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.75rem', color: '#64748b' }}>{bill.currentreading}</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#0f172a' }}>{bill.consumption} m³</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#059669' }}>{formatCurrency(bill.totalbillamount)}</TableCell>
                                                    <TableCell align="right">
                                                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                        <Tooltip title="View Receipt">
                                                          <IconButton
                                                            size="small"
                                                            sx={{ color: '#059669' }}
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              handleViewBillDetails(bill.billid, customer.customerid);
                                                            }}
                                                          >
                                                            <VisibilityIcon fontSize="small" />
                                                          </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Mark as Unpaid">
                                                          <IconButton
                                                            size="small"
                                                            sx={{ color: '#d97706' }}
                                                            onClick={(e) => {
                                                              e.stopPropagation();
                                                              setBillToMarkUnpaid(bill);
                                                              setShowUnpaidConfirm(true);
                                                            }}
                                                          >
                                                            <UndoIcon fontSize="small" />
                                                          </IconButton>
                                                        </Tooltip>
                                                      </Stack>
                                                    </TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          </Box>
                                        </Collapse>
                                      </TableCell>
                                    </TableRow>
                                  </React.Fragment>
                                );
                              })}
                            </TableBody>
                          </Table>
                          <TablePagination
                            component="div"
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            count={combinedHistory.length}
                            rowsPerPage={historyRowsPerPage}
                            page={historyPage}
                            onPageChange={(e, p) => setHistoryPage(p)}
                            onRowsPerPageChange={(e) => { setHistoryRowsPerPage(parseInt(e.target.value, 10)); setHistoryPage(0); }}
                            sx={{ borderTop: '1px solid #e2e8f0' }}
                          />
                        </TableContainer>
                      )}
                    </CardContent>
                  </>
                );
              })()}
            </Card>
          </Stack>
        );
      })()}

      {/* Sticky Fixed Bottom Multi-Customer Payment Bar */}
      {(() => {
        const globalSummary = getGlobalSelectedSummary();
        if (globalSummary.totalBillsCount === 0) return null;

        return (
          <>
            {/* Bottom Spacer so content is not obscured behind the sticky bar */}
            <Box sx={{ height: { xs: 180, lg: 120 } }} />

            <Paper
              elevation={8}
              sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 1200,
                bgcolor: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(12px)',
                borderTop: '2px solid #059669',
                boxShadow: '0 -8px 30px rgba(0, 0, 0, 0.15)',
                py: 1.5,
                px: { xs: 2, sm: 3, md: 4 }
              }}
            >
              <Container maxWidth="xl" disableGutters>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, alignItems: { xs: 'stretch', lg: 'center' }, justifyContent: 'space-between', gap: { xs: 1.5, lg: 2 } }}>
                  {/* Left Side: Summary Metrics */}
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle2" fontWeight={800} color="#0f172a">
                        Selected Payment Summary
                      </Typography>
                      <Chip 
                        label={`${globalSummary.totalBillsCount} Bill${globalSummary.totalBillsCount > 1 ? 's' : ''} Selected`}
                        size="small"
                        sx={{ bgcolor: '#ecfdf5', color: '#047857', fontWeight: 700, fontSize: '0.72rem', height: 20 }}
                      />
                      {globalSummary.customersCount > 1 && (
                        <Chip 
                          icon={<PeopleIcon sx={{ fontSize: '0.9rem !important' }} />}
                          label={`${globalSummary.customersCount} Accounts`}
                          size="small"
                          sx={{ bgcolor: '#f0f9ff', color: '#0284c7', fontWeight: 700, fontSize: '0.72rem', height: 20 }}
                        />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: { xs: 1.5, sm: 2.5, md: 3.5 } }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', display: 'block', fontWeight: 600 }}>Total Basic</Typography>
                        <Typography variant="body2" fontWeight={700} color="#0f172a" sx={{ fontSize: '0.95rem' }}>
                          {formatCurrency(globalSummary.totalBasic)}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', display: 'block', fontWeight: 600 }}>Surcharge</Typography>
                        <Typography variant="body2" fontWeight={700} color="#dc2626" sx={{ fontSize: '0.95rem' }}>
                          +{formatCurrency(globalSummary.totalSurcharge)}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', display: 'block', fontWeight: 600 }}>Discount</Typography>
                        <Typography variant="body2" fontWeight={700} color="#059669" sx={{ fontSize: '0.95rem' }}>
                          -{formatCurrency(globalSummary.totalDiscount)}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem', display: 'block', fontWeight: 600 }}>Credit Applied</Typography>
                        <Typography variant="body2" fontWeight={700} color="#0284c7" sx={{ fontSize: '0.95rem' }}>
                          -{formatCurrency(globalSummary.totalCreditApplied)}
                        </Typography>
                      </Box>

                      <Box sx={{ pl: { sm: 2 }, borderLeft: { sm: '1px solid #e2e8f0' } }}>
                        <Typography variant="caption" sx={{ color: '#475569', fontSize: '0.7rem', fontWeight: 700, display: 'block' }}>Net Amount Payable</Typography>
                        <Typography variant="h5" color="#059669" fontWeight={900} sx={{ lineHeight: 1.1 }}>
                          {formatCurrency(globalSummary.netAmountPayable)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Right Side: Action Buttons */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', lg: 'flex-end' } }}>
                    <Button 
                      variant="outlined" 
                      onClick={handleClearAllSelections} 
                      sx={{ 
                        borderRadius: 2, 
                        borderColor: '#cbd5e1', 
                        color: '#475569', 
                        fontWeight: 600,
                        textTransform: 'none',
                        px: 1.75,
                        py: 0.8,
                        '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' }
                      }}
                    >
                      Clear Selection
                    </Button>

                    <Button 
                      variant="outlined" 
                      onClick={handlePrintSelectedBills} 
                      sx={{ 
                        borderRadius: 2, 
                        borderColor: '#0284c7', 
                        color: '#0284c7', 
                        fontWeight: 600,
                        textTransform: 'none',
                        px: 1.75,
                        py: 0.8,
                        '&:hover': { bgcolor: '#f0f9ff', borderColor: '#0284c7' }
                      }}
                    >
                      Print Statement
                    </Button>

                    <Button 
                      variant="contained" 
                      onClick={() => {
                        setPendingPayment({
                          isMultiCustomer: globalSummary.customersCount > 1,
                          customers: globalSummary.customers,
                          customer: globalSummary.customers[0],
                          customerBreakdowns: globalSummary.customerBreakdowns,
                          selectedBillsData: globalSummary.allSelectedBills,
                          bills: globalSummary.allSelectedBills.map(b => b.billid),
                          count: globalSummary.totalBillsCount,
                          totalAmount: globalSummary.totalAmount,
                          totalSurcharge: globalSummary.totalSurcharge,
                          creditBalance: globalSummary.totalCreditBalance,
                          autoSelectedAmount: globalSummary.totalEffectivePayment > globalSummary.netAmountPayable ? globalSummary.totalEffectivePayment : null,
                          totalExcessPayment: globalSummary.totalExcessPayment,
                          totalEffectivePayment: globalSummary.totalEffectivePayment,
                          allBillsWithSurcharge: customerTabs.flatMap(t => (t.billsWithSurcharge || []).map(b => ({ ...b, customer: t.customer, customerid: t.customer?.customerid })))
                        });
                        setShowPaymentConfirm(true);
                      }}
                      sx={{ 
                        borderRadius: 2, 
                        bgcolor: '#059669', 
                        color: '#ffffff', 
                        fontWeight: 700, 
                        px: 2.75, 
                        py: 0.8, 
                        textTransform: 'none',
                        boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)', 
                        '&:hover': { bgcolor: '#047857' } 
                      }}
                    >
                      {globalSummary.customersCount > 1 
                        ? `Process Combined Payment (${globalSummary.customersCount} Accounts)` 
                        : 'Process Payment (OR Collection)'}
                    </Button>
                  </Box>
                </Box>
              </Container>
            </Paper>
          </>
        );
      })()}

      {/* Dialogs */}
      <PaymentConfirmDialog
        isOpen={showPaymentConfirm && pendingPayment !== null}
        onClose={() => { setShowPaymentConfirm(false); setPendingPayment(null); }}
        onConfirm={handleProcessPayment}
        billCount={pendingPayment?.count || 0}
        grandTotal={pendingPayment?.totalAmount || 0}
        totalSurcharge={pendingPayment?.totalSurcharge || 0}
        creditBalance={pendingPayment?.creditBalance || 0}
        loading={loading}
        autoSelectedAmount={pendingPayment?.autoSelectedAmount}
        customer={pendingPayment?.customer}
        customers={pendingPayment?.customers || []}
        customerBreakdowns={pendingPayment?.customerBreakdowns || []}
        selectedBillsData={pendingPayment?.selectedBillsData || []}
        selectedBillIds={pendingPayment?.bills || []}
        allBillsWithSurcharge={pendingPayment?.allBillsWithSurcharge || []}
        onPrintReceipt={handlePrintReceipt}
      />

      <ConfirmationDialog
        isOpen={showUnpaidConfirm && billToMarkUnpaid !== null}
        onClose={() => { setShowUnpaidConfirm(false); setBillToMarkUnpaid(null); }}
        onConfirm={handleMarkAsUnpaid}
        title="Mark Bill as Unpaid?"
        message={billToMarkUnpaid ? `Are you sure you want to mark Bill #${billToMarkUnpaid.billid} as unpaid?` : ''}
        subMessage="This will reverse the payment status and credit allocations."
        confirmText="Mark as Unpaid"
        cancelText="Cancel"
        type="warning"
        loading={loading}
      />

      <BillDetailsModal 
        isOpen={showBillDetails}
        onClose={() => setShowBillDetails(false)}
        bill={selectedBillDetails?.bill}
        customer={selectedBillDetails?.customer}
        history={selectedBillDetails?.history}
      />

      <Dialog 
        open={showAutoSelectDialog} 
        onClose={() => setShowAutoSelectDialog(false)}
        PaperProps={{ sx: { borderRadius: 3.5, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#0f172a' }}>Auto-Select Bills by Cash Tendered</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2, color: '#64748b' }}>
            Enter the amount tendered by consumer. The system will automatically select the oldest unpaid bills that can be fully covered by this amount.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Amount Tendered"
            type="number"
            fullWidth
            variant="outlined"
            value={autoSelectAmount}
            onChange={(e) => setAutoSelectAmount(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">₱</InputAdornment>,
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowAutoSelectDialog(false)} sx={{ borderRadius: 2, color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button onClick={handleAutoSelectByAmount} variant="contained" sx={{ borderRadius: 2, bgcolor: '#059669', color: '#ffffff', fontWeight: 600, '&:hover': { bgcolor: '#047857' } }}>Select Bills</Button>
        </DialogActions>
      </Dialog>



      <BillModal
        isOpen={showBillModal}
        onClose={() => {
          setShowBillModal(false);
          setPreSelectedCustomerForModal(null);
        }}
        mode={billModalMode}
        bill={selectedBillForModal}
        preSelectedCustomer={preSelectedCustomerForModal}
        onSuccess={handleModalSuccess}
        customerTypes={billCustomerTypes}
        surchargeSettings={surchargeSettings}
      />
      
      <ConfirmationDialog
        isOpen={showDeleteConfirm && billToDelete !== null}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message={billToDelete ? `Are you sure you want to delete bill #${billToDelete.billid}?` : ''}
        subMessage={billToDelete ? `Customer: ${billToDelete.customers?.name || 'N/A'} | Month: ${new Date(billToDelete.billedmonth).toLocaleDateString('en-PH', { year: 'numeric', month: 'long' })}` : ''}
        confirmText="Delete Bill"
        cancelText="Cancel"
        type="danger"
        loading={loading}
      />
    </Box>
  );
};

export default PaymentsPage;
